import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import translations, { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../translations';

const LanguageContext = createContext();

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

// Detect browser language and map to supported language
const detectBrowserLanguage = () => {
    const browserLang = navigator.language || navigator.userLanguage;
    // Get the language code (e.g., 'es' from 'es-ES' or 'en' from 'en-US')
    const langCode = browserLang.split('-')[0].toLowerCase();

    // Check if the browser language is supported
    const isSupported = SUPPORTED_LANGUAGES.some(lang => lang.code === langCode);

    return isSupported ? langCode : DEFAULT_LANGUAGE;
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguageState] = useState(() => {
        // Check localStorage first
        const savedLanguage = localStorage.getItem('farmway-language');
        if (savedLanguage && SUPPORTED_LANGUAGES.some(lang => lang.code === savedLanguage)) {
            return savedLanguage;
        }
        // Fall back to browser detection
        return detectBrowserLanguage();
    });

    // Persist language selection to localStorage
    const setLanguage = useCallback((newLanguage) => {
        if (SUPPORTED_LANGUAGES.some(lang => lang.code === newLanguage)) {
            setLanguageState(newLanguage);
            localStorage.setItem('farmway-language', newLanguage);
        }
    }, []);

    // Translation function
    const t = useCallback((key) => {
        const keys = key.split('.');
        let value = translations[language];

        for (const k of keys) {
            if (value && typeof value === 'object') {
                value = value[k];
            } else {
                // Fallback to Spanish if key not found
                value = undefined;
                break;
            }
        }

        // If not found in current language, try Spanish as fallback
        if (value === undefined) {
            value = translations[DEFAULT_LANGUAGE];
            for (const k of keys) {
                if (value && typeof value === 'object') {
                    value = value[k];
                } else {
                    return key; // Return key if not found anywhere
                }
            }
        }

        return value !== undefined ? value : key;
    }, [language]);

    // Get current language info
    const currentLanguage = SUPPORTED_LANGUAGES.find(lang => lang.code === language) || SUPPORTED_LANGUAGES[0];

    const value = {
        language,
        setLanguage,
        t,
        currentLanguage,
        supportedLanguages: SUPPORTED_LANGUAGES,
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};
